import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { LocationIcon, CheckIcon, StarIcon, LeafIcon, FireIcon } from '../components/Icons'
import './About.css'

const milestones = [
  { year: '2011', title: 'The Beginning', description: 'Biggies Burger was born - a small burger joint with big dreams.' },
  { year: '2016', title: 'Going Full Time', description: 'Founders left their IT careers to pursue their passion full-time.' },
  { year: '2020', title: 'Expanding Horizons', description: 'Reached 50+ locations across multiple states in India.' },
  { year: '2023', title: 'BIGGUYS Launch', description: 'Launched BIGGUYS to revolutionize the fried chicken market.' },
  { year: '2024', title: 'Rapid Growth', description: 'Became one of India\'s fastest-growing QSR chains.' },
]

export default function About() {
  const pageRef = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animation
      gsap.fromTo('.about-hero-content > *',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.3 }
      )

      // Story section animation
      gsap.fromTo('.story-content > *',
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.story-section',
            start: 'top 70%',
          }
        }
      )

      // Timeline animation
      gsap.fromTo('.milestone-item',
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.timeline-section',
            start: 'top 70%',
          }
        }
      )

      // Values animation
      gsap.fromTo('.value-card',
        { opacity: 0, scale: 0.9 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.values-section',
            start: 'top 70%',
          }
        }
      )
    }, pageRef)

    return () => ctx.revert()
  }, [])

  return (
    <div className="about-page" ref={pageRef}>
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <span className="hero-label">About Us</span>
            <h1 className="hero-title">
              Building India's Most Loved
              <span className="highlight"> Fried Chicken Brand</span>
            </h1>
            <p className="hero-description">
              From humble beginnings to becoming a household name - this is the BIGGUYS story.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-grid">
            <div className="story-image">
              <div className="story-image-container">
                <LocationIcon size={100} />
              </div>
              <div className="story-stats">
                <div className="story-stat">
                  <span className="stat-number">13+</span>
                  <span className="stat-label">Years</span>
                </div>
              </div>
            </div>
            <div className="story-content">
              <span className="section-label">Our Story</span>
              <h2 className="section-title">From Passion to Purpose</h2>
              <p>
                In 2011, Biraja Prasad Rout, an IT professional at Infosys, had a dream - to create a 
                food brand that Indians would love. What started as Biggies Burger in a small corner 
                of Bangalore has now grown into one of India's most trusted QSR chains.
              </p>
              <p>
                BIGGUYS was born in 2023 as a natural extension of this vision. We took our expertise 
                in creating amazing food experiences and channeled it into crafting the perfect fried 
                chicken - crispy, flavorful, and 100% real.
              </p>
              <p>
                Today, backed by Beamer Brands Pvt Ltd, we're on a mission to make BIGGUYS a 
                household name across India. With over 1 crore customers served and counting, 
                we're just getting started.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Our Journey</span>
            <h2 className="section-title">Milestones That Matter</h2>
          </div>
          
          <div className="timeline">
            {milestones.map((milestone, index) => (
              <div className="milestone-item" key={index}>
                <div className="milestone-year">{milestone.year}</div>
                <div className="milestone-content">
                  <h3 className="milestone-title">{milestone.title}</h3>
                  <p className="milestone-description">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">What Drives Us</span>
            <h2 className="section-title">Our Core Values</h2>
          </div>
          
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon"><CheckIcon size={45} /></div>
              <h3 className="value-title">Quality First</h3>
              <p className="value-description">
                100% real chicken, no shortcuts. We never compromise on quality.
              </p>
            </div>
            <div className="value-card">
              <div className="value-icon"><StarIcon size={45} color="#E4002B" /></div>
              <h3 className="value-title">Customer Love</h3>
              <p className="value-description">
                Every customer is family. Their satisfaction is our success.
              </p>
            </div>
            <div className="value-card">
              <div className="value-icon"><FireIcon size={45} /></div>
              <h3 className="value-title">Innovation</h3>
              <p className="value-description">
                We're always evolving, creating new flavors and experiences.
              </p>
            </div>
            <div className="value-card">
              <div className="value-icon"><LeafIcon size={45} /></div>
              <h3 className="value-title">Sustainability</h3>
              <p className="value-description">
                Ethical sourcing and responsible practices guide our operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Want to Be Part of Our Story?</h2>
            <p>Join the BIGGUYS family as a franchise partner or team member.</p>
            <div className="cta-buttons">
              <Link to="/franchise" className="btn btn-primary">Franchise Opportunities</Link>
              <Link to="/careers" className="btn btn-secondary">Join Our Team</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
